import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { snowflakeApi } from '../utils/api';

interface ApplicationSetting {
  setting_id: number;
  environment: string;
  setting_key: string;
  setting_value: string;
  setting_type: 'string' | 'int' | 'bool' | 'json' | 'float';
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface GuardrailProfile {
  profileId: string;
  profileName: string;
  profileType?: number;
  applicationId?: number;
  applicationName?: string;
  assistantId?: string;
  accessType?: number;
  tags?: string[];
}

const ApplicationSettings: React.FC = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<ApplicationSetting[]>([]);
  const [profiles, setProfiles] = useState<GuardrailProfile[]>([]);
  const [profileMap, setProfileMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentSetting, setCurrentSetting] = useState<Partial<ApplicationSetting>>({
    environment: 'development',
    setting_key: '',
    setting_value: '',
    setting_type: 'string',
    description: '',
    is_active: true
  });

  const settingTypes = ['string', 'int', 'bool', 'json', 'float'];

  const loadProfiles = async (dashboardTag: string) => {
    try {
      const authguardToken = localStorage.getItem('authguard_access_token');
      if (!authguardToken) {
        console.warn('No authguard token found, skipping profile load');
        return;
      }

      const response = await snowflakeApi.get(`/api/v1/auth/guardrail/profiles?tag=${dashboardTag}`, {
        headers: {
          'Authorization': `Bearer ${authguardToken}`
        }
      });

      const loadedProfiles = response.data as GuardrailProfile[];
      setProfiles(loadedProfiles);

      // Create a map of profileId -> profileName for easy lookup
      const map: Record<string, string> = {};
      loadedProfiles.forEach(profile => {
        if (profile.profileId && profile.profileName) {
          map[profile.profileId] = profile.profileName;
        }
      });
      setProfileMap(map);

      console.log(`Loaded ${loadedProfiles.length} profiles with tag '${dashboardTag}'`);
    } catch (err: any) {
      console.error('Error loading profiles:', err);
      // Don't show error to user, just log it - profiles are optional enhancement
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await snowflakeApi.get('/api/v1/application-settings');
      const settingsData = response.data;
      setSettings(settingsData);

      // Find dashboard_tag setting and load profiles
      const dashboardTagSetting = settingsData.find(
        (s: ApplicationSetting) => s.setting_key === 'dashboard_tag' && s.is_active
      );
      if (dashboardTagSetting) {
        await loadProfiles(dashboardTagSetting.setting_value);
      }
    } catch (err: any) {
      console.error('Error fetching application settings:', err);
      setError(err.response?.data?.detail || 'Failed to load application settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleOpenDialog = (setting?: ApplicationSetting) => {
    if (setting) {
      setEditMode(true);
      setCurrentSetting(setting);
    } else {
      setEditMode(false);
      setCurrentSetting({
        environment: 'development',
        setting_key: '',
        setting_value: '',
        setting_type: 'string',
        description: '',
        is_active: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentSetting({
      environment: 'development',
      setting_key: '',
      setting_value: '',
      setting_type: 'string',
      description: '',
      is_active: true
    });
  };

  const handleSaveSetting = async () => {
    try {
      setError(null);
      setSuccess(null);

      if (!currentSetting.setting_key || !currentSetting.setting_value) {
        setError('Setting key and value are required');
        return;
      }

      if (editMode && currentSetting.setting_id) {
        // Update existing setting
        await snowflakeApi.put(`/api/v1/application-settings/${currentSetting.setting_id}`, {
          setting_value: currentSetting.setting_value,
          setting_type: currentSetting.setting_type,
          description: currentSetting.description,
          is_active: currentSetting.is_active
        });
        setSuccess('Setting updated successfully');
      } else {
        // Create new setting
        await snowflakeApi.post('/api/v1/application-settings', {
          environment: currentSetting.environment,
          setting_key: currentSetting.setting_key,
          setting_value: currentSetting.setting_value,
          setting_type: currentSetting.setting_type,
          description: currentSetting.description,
          is_active: currentSetting.is_active
        });
        setSuccess('Setting created successfully');
      }

      handleCloseDialog();
      fetchSettings();
    } catch (err: any) {
      console.error('Error saving setting:', err);
      setError(err.response?.data?.detail || 'Failed to save setting');
    }
  };

  const handleDeleteSetting = async (settingId: number) => {
    if (!window.confirm('Are you sure you want to delete this setting?')) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      await snowflakeApi.delete(`/api/v1/application-settings/${settingId}`);
      setSuccess('Setting deleted successfully');
      fetchSettings();
    } catch (err: any) {
      console.error('Error deleting setting:', err);
      setError(err.response?.data?.detail || 'Failed to delete setting');
    }
  };

  const getValueDisplay = (value: string, type: string, settingKey: string) => {
    if (type === 'bool') {
      return value === 'true' ? 'True' : 'False';
    }
    if (type === 'json') {
      try {
        return JSON.stringify(JSON.parse(value), null, 2);
      } catch {
        return value;
      }
    }
    // Show profile name for settings that start with "profile_"
    if (settingKey.startsWith('profile_')) {
      const profileName = profileMap[value];
      if (profileName) {
        return `${profileName} (${value})`;
      }
    }
    return value;
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/')}
              sx={{ 
                mb: 2,
                color: 'text.secondary',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: 'primary.main'
                }
              }}
            >
              Back to Dashboard
            </Button>
            <Typography variant="h4" component="h1">
              Application Settings
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchSettings}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Setting
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ py: 1 }}>Setting Key</TableCell>
                    <TableCell sx={{ py: 1 }}>Value</TableCell>
                    <TableCell align="right" sx={{ py: 1 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {settings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        <Typography variant="body2" color="textSecondary" sx={{ py: 4 }}>
                          No settings found. Click "Add Setting" to create one.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    settings.map((setting) => (
                      <TableRow key={setting.setting_id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell sx={{ py: 0.75 }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500, fontSize: '0.875rem' }}>
                            {setting.setting_key}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.75 }}>
                          <Box sx={{ maxWidth: 600 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: setting.setting_type === 'json' ? 'monospace' : 'inherit',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                fontSize: '0.875rem',
                                lineHeight: 1.4
                              }}
                            >
                              {getValueDisplay(setting.setting_value, setting.setting_type, setting.setting_key)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, width: 100 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(setting)}
                            color="primary"
                            sx={{ padding: '4px' }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteSetting(setting.setting_id)}
                            color="error"
                            sx={{ padding: '4px' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editMode ? 'Edit Setting' : 'Add New Setting'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Environment"
              value={currentSetting.environment || ''}
              onChange={(e) => setCurrentSetting({ ...currentSetting, environment: e.target.value })}
              disabled={editMode}
              fullWidth
              helperText={editMode ? 'Environment cannot be changed after creation' : 'e.g., development, staging, production'}
            />
            <TextField
              label="Setting Key"
              value={currentSetting.setting_key || ''}
              onChange={(e) => setCurrentSetting({ ...currentSetting, setting_key: e.target.value })}
              disabled={editMode}
              fullWidth
              required
              helperText={editMode ? 'Setting key cannot be changed after creation' : 'Unique identifier for the setting'}
            />
            <TextField
              label="Setting Value"
              value={currentSetting.setting_value || ''}
              onChange={(e) => setCurrentSetting({ ...currentSetting, setting_value: e.target.value })}
              fullWidth
              required
              multiline={currentSetting.setting_type === 'json'}
              rows={currentSetting.setting_type === 'json' ? 4 : 1}
              helperText={
                currentSetting.setting_type === 'bool'
                  ? 'Enter "true" or "false"'
                  : currentSetting.setting_type === 'json'
                  ? 'Enter valid JSON'
                  : 'The value for this setting'
              }
            />
            <FormControl fullWidth>
              <InputLabel>Setting Type</InputLabel>
              <Select
                value={currentSetting.setting_type || 'string'}
                onChange={(e) => setCurrentSetting({ ...currentSetting, setting_type: e.target.value as any })}
                label="Setting Type"
              >
                {settingTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>The data type of the setting value</FormHelperText>
            </FormControl>
            <TextField
              label="Description"
              value={currentSetting.description || ''}
              onChange={(e) => setCurrentSetting({ ...currentSetting, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
              helperText="Optional description of what this setting controls"
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={currentSetting.is_active ? 'active' : 'inactive'}
                onChange={(e) => setCurrentSetting({ ...currentSetting, is_active: e.target.value === 'active' })}
                label="Status"
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
              <FormHelperText>Inactive settings are ignored by the application</FormHelperText>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveSetting} variant="contained" color="primary">
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ApplicationSettings;
